/** Computes the hero's final stats from level, gear, passives and buffs. */
import { EMPTY_PASSIVES, PASSIVE_BY_ID, type PassiveEffects } from '../data/skills';
import type { WeaponKind } from '../art/pixel/types';
import { WEAPON_FEEL } from '../data/items';
import { SETS, setPieceCounts, type SetPower } from '../data/sets';
import { defenseReduction, heroBaseStats } from './balance';
import { ASC_NODE_BY_ID } from '../data/ascendancy';
import { itemStats } from './items';
import { paragonBonuses } from './paragon';
import type { SaveData } from './state';
import { BASE_FLASK_HP, BASE_FLASK_MP } from './state';
import type { Element, Item, LegendaryId, SetId, StatKey, Stats } from './types';
import { activeCharms, equipTargetFor, weaponElement } from './items';
import { EQUIP_SLOTS, OFFHAND_WEAPON_BASE } from './types';

/** Timed buffs (seconds remaining). */
export interface Buffs {
  might: number;
  guard: number;
  fortune: number;
  /** Blood Rite skill. */
  rite: number;
  /** Blood Rite's damage bonus while active. */
  riteBonus: number;
  /** Aether Overdrive: after the Aether Cannon fires. */
  overdrive: number;
  /** Mask of the Berserker: frenzy time left and stacks. */
  frenzy: number;
  frenzyStacks: number;
}

export const NO_BUFFS: Readonly<Buffs> = Object.freeze({
  might: 0,
  guard: 0,
  fortune: 0,
  rite: 0,
  riteBonus: 0,
  overdrive: 0,
  frenzy: 0,
  frenzyStacks: 0,
});

/** Aether Overdrive (after the Surge): damage and attack speed bonus, and how long it lasts. */
export const OVERDRIVE = { dmg: 0.3, atkSpeed: 0.2, duration: 10 };
/** Frenzy (Mask of the Berserker) per stack. */
export const FRENZY = { perStack: 0.05, max: 6, duration: 5 };

export interface HeroStats extends Stats {
  legendaries: Set<LegendaryId>;
  /** Ascendancy notables taken (special effects are checked in combat). */
  asc: Set<string>;
  passives: PassiveEffects;
  flaskHpMax: number;
  flaskMpMax: number;
  flaskPotency: number;
  /** Incoming damage multiplier. */
  damageTaken: number;
  /** The weapon's element and the fraction of its damage converted to it. */
  element: Element;
  elemConv: number;
  /** Set pieces worn per set, and the set powers they unlock. */
  setCounts: Map<SetId, number>;
  setPowers: Set<SetPower>;
}

export function passiveEffects(s: SaveData): PassiveEffects {
  const out: PassiveEffects = { ...EMPTY_PASSIVES };
  for (const [id, rank] of Object.entries(s.hero.passives)) {
    const def = PASSIVE_BY_ID[id];
    if (!def || rank <= 0) continue;
    for (const [k, v] of Object.entries(def.effect) as [keyof PassiveEffects, number][]) out[k] += v * rank;
  }
  return out;
}

export function computeHeroStats(s: SaveData, buffs?: Buffs): HeroStats {
  const base = heroBaseStats(s.hero.level);
  const gear: Partial<Stats> = {};
  const legendaries = new Set<LegendaryId>();
  const worn = EQUIP_SLOTS.map((sl) => ({ it: s.equipment[sl], sl }));
  const sources = [...worn, ...activeCharms(s).map((it) => ({ it, sl: null }))];
  for (const { it, sl } of sources) {
    if (!it) continue;
    // a second weapon in the off hand never attacks: it adds half its base stats
    const baseMult = sl === 'offhand' && it.slot === 'weapon' ? OFFHAND_WEAPON_BASE : 1;
    for (const [k, v] of Object.entries(itemStats(it, baseMult)) as [StatKey, number][])
      gear[k] = (gear[k] ?? 0) + v;
    if (it.legendary) legendaries.add(it.legendary);
  }
  const p = passiveEffects(s);
  // item sets
  const setCounts = setPieceCounts(EQUIP_SLOTS.map((sl) => s.equipment[sl]));
  const setPowers = new Set<SetPower>();
  const setStats: Partial<Stats> = {};
  for (const [id, n] of setCounts) {
    for (const b of SETS[id].bonuses) {
      if (n < b.pieces) continue;
      for (const [k, v] of Object.entries(b.stats ?? {}) as [StatKey, number][])
        setStats[k] = (setStats[k] ?? 0) + v;
      for (const [k, v] of Object.entries(b.passives ?? {}) as [keyof PassiveEffects, number][]) p[k] += v;
      if (b.power) setPowers.add(b.power);
    }
  }
  const para = paragonBonuses(s.hero.paragon);
  for (const [k, v] of Object.entries(para.passives) as [keyof PassiveEffects, number][]) p[k] += v;
  const st: Stats = { ...base };
  for (const [k, v] of Object.entries(gear) as [StatKey, number][]) st[k] += v;
  for (const [k, v] of Object.entries(setStats) as [StatKey, number][]) st[k] += v;
  for (const [k, v] of Object.entries(para.stats) as [StatKey, number][]) st[k] += v;
  if (legendaries.has('robe_archmage')) p.magPct += 0.4;
  const asc = new Set<string>();
  let ascTaken = 1;
  for (const id of s.ascendancy?.nodes ?? []) {
    const node = ASC_NODE_BY_ID[id];
    if (!node || node.cls !== s.ascendancy.cls) continue;
    asc.add(id);
    for (const [k, v] of Object.entries(node.stats ?? {}) as [StatKey, number][]) st[k] += v;
    for (const [k, v] of Object.entries(node.passives ?? {}) as [keyof PassiveEffects, number][]) p[k] += v;
    ascTaken *= node.damageTaken ?? 1;
  }

  st.atk *= 1 + p.atkPct;
  st.mag *= 1 + p.magPct;
  st.def *= 1 + p.defPct;
  st.maxMp += p.mpFlat;
  st.crit += p.crit;
  st.critDmg += p.critDmg;
  st.lifesteal += p.lifesteal;
  st.atkSpeed += p.atkSpeed;
  st.cdr += p.cdr;
  st.skillDmg += p.skillDmg;
  st.mpRegen *= 1 + p.mpRegenPct;
  st.dodgeCost += p.dodgeCost;
  st.moveSpeed += p.moveSpeed;
  st.burnChance += p.burnChance;

  let hpMult = 1 + p.hpPct;
  if (legendaries.has('mountain_heart')) hpMult += 0.25;
  if (legendaries.has('hollow_crown')) hpMult -= 0.15;
  st.maxHp *= hpMult;
  st.hpRegen += st.maxHp * p.hpRegenPctMax;
  if (legendaries.has('windwalkers')) st.dodgeCost += 0.5;

  let damageTaken = para.damageTaken * ascTaken * (1 - Math.min(0.6, p.damageReduction));
  if (legendaries.has('robe_archmage')) damageTaken *= 1.2;
  if (legendaries.has('aegis_of_ages')) damageTaken *= 0.85;
  if (buffs && buffs.might > 0) st.dmgBonus += 0.2;
  if (buffs && buffs.guard > 0) damageTaken *= 0.8;
  if (buffs && buffs.fortune > 0) {
    st.magicFind += 0.5;
    st.goldFind += 0.5;
  }
  if (buffs && buffs.rite > 0) {
    st.dmgBonus += buffs.riteBonus;
    st.lifesteal += 0.05;
  }
  if (buffs && buffs.overdrive > 0) {
    st.dmgBonus += OVERDRIVE.dmg;
    st.atkSpeed += OVERDRIVE.atkSpeed;
  }
  if (buffs && buffs.frenzy > 0) {
    st.dmgBonus += FRENZY.perStack * buffs.frenzyStacks;
    st.atkSpeed += FRENZY.perStack * buffs.frenzyStacks;
  }

  // caps
  st.crit = Math.max(0, Math.min(0.75, st.crit));
  st.cdr = Math.min(0.5, st.cdr);
  st.lifesteal = Math.min(0.2, st.lifesteal);
  st.atkSpeed = Math.max(-0.5, Math.min(1, st.atkSpeed));
  st.moveSpeed = Math.max(-0.4, Math.min(0.5, st.moveSpeed));
  st.def = Math.max(0, st.def);
  st.mpRegen = Math.max(0, st.mpRegen);
  st.goldFind = Math.max(-0.9, st.goldFind);
  st.dodgeCost = Math.min(0.75, st.dodgeCost);
  st.maxHp = Math.max(1, Math.round(st.maxHp));
  st.maxMp = Math.round(st.maxMp);

  const flaskBonus = Math.round(p.flaskCharges);
  const we = weaponElement(s.equipment.weapon);
  return {
    ...st,
    legendaries,
    asc,
    element: we?.element ?? 'physical',
    elemConv: we?.conv ?? 0,
    setCounts,
    setPowers,
    passives: p,
    flaskHpMax: BASE_FLASK_HP + s.hero.flaskUpgrades + flaskBonus,
    flaskMpMax: BASE_FLASK_MP + Math.floor(s.hero.flaskUpgrades / 2) + flaskBonus,
    flaskPotency: 1 + p.flaskPotency,
    damageTaken,
  };
}

/** Rough "power level" shown in the UI (like modern ARPG gear score). */
export function powerRating(st: Stats): number {
  const offense =
    Math.max(st.atk, st.mag) * (1 + st.crit * st.critDmg) * (1 + st.atkSpeed * 0.5) * (1 + st.dmgBonus);
  const defense = st.maxHp / 10 + st.def;
  return Math.round(offense * 2 + defense);
}

// ------------------------------------------------------------ derived ----

export interface DerivedStats {
  /** Weapon hit range before crits (per basic-attack hit, combo-averaged). */
  hitMin: number;
  hitMax: number;
  attacksPerSec: number;
  /** Expected basic-attack damage per second (crits, bonuses; before enemy defense). */
  dps: number;
  critMultiplier: number;
  /** Fraction of damage blocked by defense against an enemy of the hero's level. */
  damageReduction: number;
  /** Raw damage needed to kill the hero (HP adjusted for mitigation). */
  toughness: number;
  /** Life regained per second from regeneration + lifesteal at full DPS. */
  recovery: number;
  moveSpeed: number;
  rollCost: number;
  flaskHeal: number;
  /** Primary damage stat (Magic for staves, Attack otherwise). */
  scaling: 'atk' | 'mag';
}

/** Numbers for the character sheet, like the "details" panel in Diablo or Path of Exile. */
export function deriveStats(level: number, st: HeroStats, weapon: WeaponKind): DerivedStats {
  const feel = WEAPON_FEEL[weapon];
  const speed = 1 + st.atkSpeed;
  const combo = [
    { mult: 1, time: feel.windup + feel.active + feel.recover },
    { mult: 1.1, time: feel.windup + feel.active + feel.recover },
    { mult: 1.5, time: feel.windup * 1.4 + feel.active + feel.recover * 1.35 },
  ];
  const cycle = combo.reduce((n, c) => n + c.time, 0) / speed;
  const scaling = feel.stat;
  const power = scaling === 'mag' ? st.mag : st.atk;
  const avgMult = combo.reduce((n, c) => n + c.mult, 0) / combo.length;
  // the elemental part of each hit gets the Elemental Damage bonus
  const elemental = 1 - st.elemConv + st.elemConv * (1 + st.elemDmg);
  const hitAvg = power * feel.dmg * avgMult * (1 + st.dmgBonus) * elemental;
  const critMultiplier = 1 + st.crit * st.critDmg;
  const attacksPerSec = combo.length / cycle;
  const dps = hitAvg * critMultiplier * attacksPerSec;
  const damageReduction = defenseReduction(st.def, level);
  const toughness = st.maxHp / Math.max(0.05, 1 - damageReduction) / st.damageTaken;
  return {
    hitMin: Math.round(hitAvg * 0.9),
    hitMax: Math.round(hitAvg * 1.1),
    attacksPerSec,
    dps,
    critMultiplier,
    damageReduction,
    toughness,
    recovery: st.hpRegen + st.lifesteal * dps,
    moveSpeed: 82 * (1 + st.moveSpeed),
    rollCost: 28 * (1 - st.dodgeCost),
    flaskHeal: st.maxHp * 0.45 * st.flaskPotency,
    scaling,
  };
}

/** How DPS / toughness / recovery would change if `item` replaced the gear in its slot. */
export function equipDelta(
  s: SaveData,
  item: Item,
  buffs?: Buffs,
): { dps: number; toughness: number; recovery: number; dpsPct: number; toughPct: number } | null {
  const target = equipTargetFor(s, item);
  if (!target) return null;
  const before = computeHeroStats(s, buffs);
  const trial: SaveData = {
    ...s,
    equipment: { ...s.equipment, [target]: item },
    inventory: s.inventory.filter((i) => i !== item),
  };
  const after = computeHeroStats(trial, buffs);
  const kindBefore = s.equipment.weapon?.kind ?? 'sword';
  const kindAfter = trial.equipment.weapon?.kind ?? 'sword';
  const a = deriveStats(s.hero.level, before, kindBefore);
  const b = deriveStats(s.hero.level, after, kindAfter);
  return {
    dps: b.dps - a.dps,
    toughness: b.toughness - a.toughness,
    recovery: b.recovery - a.recovery,
    dpsPct: a.dps > 0 ? (b.dps - a.dps) / a.dps : 0,
    toughPct: a.toughness > 0 ? (b.toughness - a.toughness) / a.toughness : 0,
  };
}
